import { getBestDeliveryZoneWithTaxDetails } from "./getBestDeliveryZone"
import type { Point, DeliveryZone } from "@lib/zod/deliverySchema";
import type { CustomServiceFee } from "../zod/serviceFee";
import type { SubTotalResult } from "./calculateSubTotal";
import { calculateCustomServiceFees } from "./calculateSubTotal";
import type { TaxSettings } from "../zod/taxSchema";
import type { DeliveryteamOrderInfoType } from "../zod/cartItemState";
import type { FeeTransformType } from "../zod/FeeTransformSchema";
import { applyFeeTransform } from "./applyFeeTransform";
import { fixMath } from "./fixMath";

export const calculateDeliveryteamSubTotal = (
    deliveryLocation: Point,
    pickupLocation: Point,
    deliveryZones: DeliveryZone[],
    taxSettings: TaxSettings,
    customServiceFees: CustomServiceFee[],
    deliveryteamOrderInfo?: DeliveryteamOrderInfoType,
    feeTransform?: FeeTransformType | null,
): SubTotalResult => {
    // Initialize result
    const result: SubTotalResult = {
        itemTotal: 0,
        tax: 0,
        totalWithTax: 0,
        shownFee: 0,
        calculatedCustomServiceFees: [],
        bestDeliveryZone: null
    };

    result.bestDeliveryZone = getBestDeliveryZoneWithTaxDetails(
        { lat: deliveryLocation.lat, lng: deliveryLocation.lng },
        deliveryZones,
        { lat: pickupLocation.lat, lng: pickupLocation.lng },
        taxSettings
    );

    result.totalWithTax = result.bestDeliveryZone?.totalWithTax ?? 0;
    result.itemTotal = result.bestDeliveryZone?.itemTotal ?? 0;
    result.tax = result.bestDeliveryZone?.tax ?? 0;
    result.shownFee = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
        result.itemTotal : result.totalWithTax;

    // Apply feeTransform to delivery zone fee before custom service fees
    // so that taxes and partner fees downstream are calculated on the transformed amount
    if (feeTransform && result.bestDeliveryZone) {
        const transformed = applyFeeTransform(result.shownFee, feeTransform);
        if (transformed.shownFee <= 0 || result.shownFee <= 0) {
            // "Free" — zero out delivery fee and skip custom service fees entirely
            result.totalWithTax = 0;
            result.itemTotal = 0;
            result.tax = 0;
            result.shownFee = 0;
            result.bestDeliveryZone = {
                ...result.bestDeliveryZone,
                totalWithTax: 0,
                itemTotal: 0,
                tax: 0,
                shownFee: 0,
            };
            return result;
        }
        // Partial transform — scale proportionally so tax ratio stays accurate
        const ratio = fixMath.divide(transformed.shownFee, result.shownFee);
        result.totalWithTax = fixMath.multiply(result.totalWithTax, ratio);
        result.itemTotal = fixMath.multiply(result.itemTotal, ratio);
        result.tax = fixMath.multiply(result.tax, ratio);
        result.shownFee = transformed.shownFee;
        result.bestDeliveryZone = {
            ...result.bestDeliveryZone,
            totalWithTax: result.totalWithTax,
            itemTotal: result.itemTotal,
            tax: result.tax,
            shownFee: result.shownFee,
        };
    }

    // override editable service fees if deliveryteamOrderInfo has values for the id
    for (let index = 0; index < customServiceFees.length; index++) {
        const cServiceFee = customServiceFees[index];
        if (!cServiceFee) continue;
        if (!cServiceFee.id) continue;
        if (!customServiceFees[index]!.isEditable) continue;
        const orderInfoDefinedValues = deliveryteamOrderInfo?.editableServiceFeeRates?.[cServiceFee.id];
        if (orderInfoDefinedValues) {
            customServiceFees[index]!.ratePerOrder = orderInfoDefinedValues.ratePerOrder;
            customServiceFees[index]!.feePerOrder = orderInfoDefinedValues.feePerOrder;
        } else if (deliveryteamOrderInfo) {
            if (!deliveryteamOrderInfo.editableServiceFeeRates) {
                deliveryteamOrderInfo.editableServiceFeeRates = {};
            }
            if (!deliveryteamOrderInfo.editableServiceFeeRates[cServiceFee.id]) {
                deliveryteamOrderInfo.editableServiceFeeRates[cServiceFee.id] = {}
                if (cServiceFee.ratePerOrder) {
                    deliveryteamOrderInfo.editableServiceFeeRates[cServiceFee.id]!.ratePerOrder = cServiceFee.ratePerOrder;
                }
                if (cServiceFee.feePerOrder) {
                    deliveryteamOrderInfo.editableServiceFeeRates[cServiceFee.id]!.feePerOrder = cServiceFee.feePerOrder;
                }
            }
        }
    }

    // I do not have order type for deliveryteamOrders, appliedOrderTypesArray is not used here
    return calculateCustomServiceFees(result, taxSettings, customServiceFees);
};